import React from "react";
import { useTheme } from "@/app/providers/ThemeContext";

const getShimmerStyle = (theme: "light" | "dark"): React.CSSProperties => ({
  backgroundImage:
    theme === "dark"
      ? "linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.03) 100%)"
      : "linear-gradient(90deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.08) 50%, rgba(0,0,0,0.05) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s ease-in-out infinite",
});

/** Injects the @keyframes rule once. */
export const SkeletonStyles = () => (
  <style
    dangerouslySetInnerHTML={{
      __html: `
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}`,
    }}
  />
);

/* ── Base shimmer rectangle ───────────────────────────────────────────── */
export const Skeleton: React.FC<{
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  className?: string;
  style?: React.CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>> = ({
  width = "100%",
  height = 16,
  borderRadius = 4,
  className,
  style,
  ...props
}) => {
  const { theme } = useTheme();
  const shimmerStyle = getShimmerStyle(theme);

  return (
    <div
      className={className}
      style={{
        width,
        height,
        borderRadius,
        ...shimmerStyle,
        ...style,
      }}
      {...props}
    />
  );
};

/* ── Text-line skeleton ───────────────────────────────────────────────── */
export const SkeletonText: React.FC<{
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
}> = ({ width = "100%", height = 14, style }) => (
  <Skeleton width={width} height={height} borderRadius={3} style={style} />
);

/* ── Card-shaped skeleton (matches portal stat cards) ─────────────────── */
export const SkeletonCard: React.FC<{
  style?: React.CSSProperties;
}> = ({ style }) => {
  const { theme } = useTheme();
  const bgColor = theme === "dark" ? "#111111" : "#f0f1f3";
  const borderColor = theme === "dark" ? "rgba(0,184,219,0.10)" : "rgba(0,0,0,0.1)";

  return (
    <div
      style={{
        backgroundImage: bgColor,
        borderRadius: 8,
        border: `1px solid ${borderColor}`,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        ...style,
      }}
    >
      <Skeleton width={90} height={10} borderRadius={3} />
      <Skeleton width={72} height={28} borderRadius={4} />
      <Skeleton width="100%" height={3} borderRadius={3} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Skeleton width={60} height={10} borderRadius={3} />
        <Skeleton width={30} height={10} borderRadius={3} />
      </div>
    </div>
  );
};

/* ── Chart-area skeleton (taller, wider) ──────────────────────────────── */
export const SkeletonChart: React.FC<{
  height?: string | number;
  style?: React.CSSProperties;
}> = ({ height = 220, style }) => {
  const { theme } = useTheme();
  const shimmerStyle = getShimmerStyle(theme);

  return (
    <div
      style={{
        width: "100%",
        height,
        borderRadius: 6,
        ...shimmerStyle,
        ...style,
      }}
    />
  );
};